import staticArtists from "../../programme/artists.json";
import staticDays from "../../programme/days.json";
import staticFestivalInfo from "../../programme/festivalInfo.json";
import staticPerformances from "../../programme/performances.json";
import staticStages from "../../programme/stages.json";
import { isRemoteDataSource } from "../config/dataSource";
import type { Artist, Day, FestivalInfo, Performance, Stage } from "../types";

// State
let artistsData: Artist[] = staticArtists as unknown as Artist[];
let daysData: Day[] = staticDays as Day[];
let stagesData: Stage[] = staticStages as Stage[];
let performancesData: Performance[] = staticPerformances as Performance[];
let festivalInfoData: FestivalInfo = staticFestivalInfo as FestivalInfo;

let artistsMap = new Map<string, Artist>(artistsData.map((a) => [a.id, a]));
let daysMap = new Map<string, Day>(daysData.map((d) => [d.id, d]));
let stagesMap = new Map<string, Stage>(stagesData.map((s) => [s.id, s]));

// Listeners for React context
type Listener = () => void;
const listeners = new Set<Listener>();

export const subscribeToData = (listener: Listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const notifyListeners = () => {
  for (const listener of listeners) {
    listener();
  }
};

const rebuildMaps = () => {
  artistsMap = new Map(artistsData.map((a) => [a.id, a]));
  daysMap = new Map(daysData.map((d) => [d.id, d]));
  stagesMap = new Map(stagesData.map((s) => [s.id, s]));
};
let lastCheckTime = 0;
const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes

export const reloadFestivalData = async (force = false) => {
  if (!isRemoteDataSource()) return;

  const now = Date.now();
  if (!force && now - lastCheckTime < CACHE_DURATION) {
    console.debug("[dataService] Skipping tainted check, cached data is still fresh.");
    return;
  }
  lastCheckTime = now;

  try {
    console.debug("[dataService] Checking if data is tainted...");
    const taintedResponse = await fetch("/api/tainted");
    if (taintedResponse.ok) {
      const { tainted } = await taintedResponse.json();
      if (!tainted) {
        console.debug(
          "[dataService] Data is not tainted. Skipping remote fetch and using bundled static JSON.",
        );
        artistsData = staticArtists as unknown as Artist[];
        daysData = staticDays as Day[];
        stagesData = staticStages as Stage[];
        performancesData = staticPerformances as Performance[];
        festivalInfoData = staticFestivalInfo as FestivalInfo;
        rebuildMaps();
        notifyListeners();
        return;
      }
    }
  } catch (error) {
    console.warn(
      "[dataService] Failed to check tainted status, falling back to remote fetch.",
      error,
    );
  }

  try {
    const url = force ? `/api/data?v=${Date.now()}` : "/api/data";
    console.debug(`[dataService] Fetching festival data from ${url} (force=${force})`);
    const response = await fetch(url, {
      ...(force && { cache: "no-store" }),
    });

    if (!response.ok) throw new Error(`API returned ${response.status}`);
    const data = await response.json();

    artistsData = data.artists || staticArtists;
    daysData = data.days || staticDays;
    stagesData = data.stages || staticStages;
    performancesData = data.performances || staticPerformances;
    festivalInfoData = data.festivalInfo || staticFestivalInfo;

    rebuildMaps();
    notifyListeners();
    console.debug(
      `[dataService] Festival data loaded: ${artistsData.length} artists, ${performancesData.length} performances, ${daysData.length} days`,
    );
  } catch (error) {
    console.warn("[dataService] Failed to load remote data, falling back to bundled JSON.", error);
    // On failure, keep whatever we already have (or the static defaults)
  }
};

// In-flight promise — deduplicates concurrent loader calls within the same navigation.
// Resets to null after each completed fetch so the next navigation gets fresh data.
let inFlightPromise: Promise<void> | null = null;

export const loadFestivalData = async () => {
  if (isRemoteDataSource()) {
    if (!inFlightPromise) {
      inFlightPromise = reloadFestivalData().finally(() => {
        inFlightPromise = null;
      });
    }
    await inFlightPromise;
  }
};

// Data access functions
export const getArtistById = (id: string): Artist | undefined => artistsMap.get(id);
export const getDayById = (id: string): Day | undefined => daysMap.get(id);
export const getStageById = (id: string): Stage | undefined => stagesMap.get(id);

export const getPerformancesByDayId = (dayId: string): Performance[] =>
  performancesData.filter((performance) => performance.dayId === dayId);

export const getPerformancesByArtistId = (artistId: string): Performance[] =>
  performancesData.filter((performance) => performance.artistId === artistId);

export const getPerformancesByStageId = (stageId: string): Performance[] =>
  performancesData.filter((performance) => performance.stageId === stageId);

export const getEventMeta = (): FestivalInfo => festivalInfoData;
export const getAllDays = (): Day[] => daysData;
export const getAllArtists = (): Artist[] => artistsData;
export const getAllStages = (): Stage[] => stagesData;
export const getPerformanceById = (id: string): Performance | undefined =>
  performancesData.find((performance) => performance.id === id);
export const getAllPerformances = () => performancesData;

export const getStageColor = (stageId: string): string => {
  switch (stageId) {
    case "nagy":
    case "stage-nagy":
    case "nagyszinpad":
      return "#F1773E"; // Orange
    case "kerti":
    case "stage-kerti":
    case "kerti-szinpad":
      return "#3CD070"; // Light Green
    case "kis":
    case "stage-kis":
    case "kisterem":
      return "#22D3BA"; // Turquoise
    case "cok":
    case "stage-cok":
    case "cokxpon":
      return "#F7C4FF"; // Pink
    case "kerti-dj":
      return "#FFC34F"; // Yellow
    case "bogracs":
      return "#F99532"; // Light Orange
    case "tilos-caravan-studio":
      return "#C0B5FC"; // Light Purple
    case "merch":
      return "#8667B9"; // Purple
    default:
      return "#FFFCE8"; // White
  }
};

export const getStageColorAndGlow = (stageId: string): { color: string; glow: string } => {
  const color = getStageColor(stageId);
  let glow = "rgba(255, 252, 232, 0.5)"; // default white/cream glow
  switch (color) {
    case "#F1773E": // Orange
      glow = "rgba(241, 119, 62, 0.5)";
      break;
    case "#3CD070": // Light Green
      glow = "rgba(60, 208, 112, 0.5)";
      break;
    case "#22D3BA": // Turquoise
      glow = "rgba(34, 211, 186, 0.5)";
      break;
    case "#F7C4FF": // Pink
      glow = "rgba(247, 196, 255, 0.5)";
      break;
    case "#FFC34F": // Yellow
      glow = "rgba(255, 195, 79, 0.5)";
      break;
    case "#F99532": // Light Orange
      glow = "rgba(249, 149, 50, 0.5)";
      break;
    case "#C0B5FC": // Light Purple
      glow = "rgba(192, 181, 252, 0.5)";
      break;
    case "#8667B9": // Purple
      glow = "rgba(134, 103, 185, 0.5)";
      break;
  }
  return { color, glow };
};
